import React, { useState } from "react";

import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import {
  DashboardComponentGroup,
  DashboardComponentGroupLayout,
} from "api/model/dashboard/DashboardComponentGroup";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowExpansionTemplate,
  DataTableRowReorderEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import ComponentSubTableEditor from "./ComponentSubTableEditor";
import GroupLayoutConfiguration from "./GroupLayoutConfiguration";

type GroupSubTableEditorProps = {
  tab: DashboardTabModel;
  onChange: (tab: DashboardTabModel, index: number) => void;
  tabIndex: number;
};

interface GroupWithTitle extends DashboardComponentGroup {
  title: string;
}

const GroupSubTableEditor: React.FC<GroupSubTableEditorProps> = ({ tab, tabIndex, onChange }) => {
  const tempGroups: Array<GroupWithTitle> = tab.componentGroups.map((ob, index) => ({
    ...ob,
    title: `${T("word.component-group")} ${index + 1}`,
  }));
  const [expandedGroupRows, setExpandedGroupRows] = useState<any[] | DataTableExpandedRows | undefined>([
    ...tempGroups,
  ]);

  const groupChange = (group: DashboardComponentGroup, index: number) => {
    tab.componentGroups[index] = group;
    onChange(tab, tabIndex);

    setExpandedGroupRows(prevState => {
      if (!Array.isArray(prevState)) {
        return tab.componentGroups;
      }

      if (!group.components || (group.components && group.components.length === 0)) {
        return prevState.filter(g => g !== group);
      }

      if (!prevState.some(g => g.index === index)) {
        return [...prevState, group];
      }

      return prevState.map(ob => (ob.index === index ? group : ob));
    });
  };

  const onAddComponentClick = (rowData: GroupWithTitle, index: number) => {
    const comp = Object.freeze({ title: "", type: "", config: {} }) as unknown as IDashboardComponent;
    const group: DashboardComponentGroup = { ...rowData, components: [...rowData.components, comp] };
    tab.componentGroups[index] = group;
    onChange(tab, tabIndex);

    setExpandedGroupRows(oldExpands => [
      ...((oldExpands as any[]) ?? []).filter(oldGroup => oldGroup.title !== rowData.title),
      group,
    ]);
  };

  const onRemoveGroupClick = (index: number) => {
    tab.componentGroups.splice(index, 1);
    onChange(tab, tabIndex);
  };

  const onRowReorder = (e: DataTableRowReorderEvent<GroupWithTitle[]>) => {
    tab.componentGroups = e.value;
    onChange(tab, tabIndex);
  };

  const confirmGroupDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-group"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveGroupClick(index),
    });
  };

  const editButton = (rowData: DashboardComponentGroup, index: number) => (
    <EditGroupButton groupChange={groupChange} rowData={rowData} index={index} />
  );

  const addButton = (rowData: GroupWithTitle, index: number) => (
    <Button
      icon="pi pi-plus"
      label={T("word.component")}
      className="p-button-sm p-button-success"
      onClick={() => {
        onAddComponentClick(rowData, index);
      }}
    />
  );

  const removeButton = (index: number) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-danger"
      onClick={event => confirmGroupDelete(event, index)}
    />
  );

  const renderExpansionTemplate = (data: GroupWithTitle, options: DataTableRowExpansionTemplate) => {
    return <ComponentSubTableEditor group={data} groupIndex={options.index} onChange={groupChange} />;
  };

  return (
    <div className="group-table">
      <DataTable
        value={tempGroups}
        reorderableRows
        onRowReorder={onRowReorder}
        size="small"
        expandedRows={expandedGroupRows}
        onRowToggle={e => setExpandedGroupRows(e.data as GroupWithTitle[])}
        rowExpansionTemplate={renderExpansionTemplate}
      >
        <Column expander={rowData => rowData.components?.length > 0} style={{ width: "3em" }} />
        <Column
          field="title"
          header={T("word.groups")}
          body={(_, options) => `${T("word.component-group")} ${options.rowIndex + 1}`}
        />
        <Column style={{ width: "0" }} body={(data, options) => addButton(data, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(rowData, options) => editButton(rowData, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(_, options) => removeButton(options.rowIndex)} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

export default GroupSubTableEditor;

const EditGroupButton: React.FC<{
  rowData: DashboardComponentGroup;
  index: number;
  groupChange: (group: DashboardComponentGroup, index: number) => void;
}> = ({ rowData, index, groupChange }) => {
  const [dialog, setDialog] = useState(false);

  const onGroupLayoutChange = (newLayout: DashboardComponentGroupLayout) => {
    groupChange({ ...rowData, layout: newLayout }, index);
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
      <Dialog
        visible={dialog}
        onHide={() => setDialog(false)}
        header={T("label.component-group-configuration", { index: index + 1 })}
        style={{ width: "min(100%, 1000px)" }}
      >
        <GroupLayoutConfiguration
          layout={rowData.layout}
          onChange={onGroupLayoutChange}
          closeDialog={() => setDialog(false)}
        />
      </Dialog>
    </>
  );
};
